<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";

const props = withDefaults(
  defineProps<{
    visible: boolean;
    icon: string;
    title?: string;
    steps: { key: string; label: string }[];
    currentStep: string | null;
    message?: string;
    errorMessage?: string;
  }>(),
  {
    title: undefined,
    message: undefined,
    errorMessage: undefined,
  },
);

const emit = defineEmits<{
  (e: "close"): void;
}>();

const { t } = useTranslation();

const currentStepIndex = computed(() => {
  if (!props.currentStep) return -1;
  if (props.currentStep === "done") return props.steps.length;
  return props.steps.findIndex((s) => s.key === props.currentStep);
});

const getStepStatus = (stepIndex: number) => {
  if (currentStepIndex.value === -1) return "pending";
  if (stepIndex < currentStepIndex.value) return "completed";
  if (stepIndex === currentStepIndex.value) return "active";
  return "pending";
};
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="visible" class="ai-overlay">
        <div class="overlay-content">
          <!-- AI Icon with pulse animation -->
          <div class="ai-icon-container">
            <div class="pulse-ring" />
            <div class="pulse-ring delay-1" />
            <div class="ai-icon">
              <i :class="icon" />
            </div>
          </div>

          <h3 v-if="title" class="overlay-title">{{ title }}</h3>

          <!-- Steps list -->
          <div class="steps-list">
            <div
              v-for="(step, index) in steps"
              :key="step.key"
              class="step-item"
              :class="getStepStatus(index)"
            >
              <div class="step-indicator">
                <i
                  v-if="getStepStatus(index) === 'completed'"
                  class="bi-check-lg"
                />
                <div
                  v-else-if="getStepStatus(index) === 'active'"
                  class="spinner"
                />
                <div v-else class="circle" />
              </div>
              <span class="step-label">{{ step.label }}</span>
            </div>
          </div>

          <!-- Message -->
          <p v-if="errorMessage" class="overlay-message error-message">
            {{ errorMessage }}
          </p>
          <p v-else class="overlay-message">
            {{ message || t("common:pleaseWait") }}
          </p>

          <!-- Minimize button -->
          <button class="close-btn" @click="emit('close')">
            {{ t("report:ai.overlay.minimize") }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.ai-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 32px;
}

.ai-icon-container {
  position: relative;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid rgba(139, 92, 246, 0.5);
  animation: pulse 1.5s ease-out infinite;
}

.pulse-ring.delay-1 {
  animation-delay: 0.75s;
}

@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

.ai-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 36px;
  z-index: 1;
  animation: iconPulse 2s ease-in-out infinite;
}

@keyframes iconPulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.overlay-title {
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  text-align: center;
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 380px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;

  &.active {
    background-color: rgba(139, 92, 246, 0.2);

    .step-label {
      color: #a78bfa;
    }
  }

  &.completed {
    .step-indicator {
      background-color: #22c55e;
      color: white;
    }

    .step-label {
      color: rgba(255, 255, 255, 0.6);
    }
  }

  &.pending {
    opacity: 0.5;
  }
}

.step-indicator {
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 14px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #8b5cf6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.circle {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
}

.step-label {
  color: white;
  font-size: 14px;
  font-weight: 500;
}

.overlay-message {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin: 0;
  text-align: center;
  max-width: 300px;

  &.error-message {
    color: #ef4444;
  }
}

.close-btn {
  margin-top: 16px;
  padding: 8px 20px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
